/**
 * Admin → Server Usage
 * ──────────────────────────────────────────────────────────────────────────
 * Route: /admin/server
 * Roles: super_admin, manager  (raw-debug tab is super_admin only)
 *
 * Layout:
 *  • Sticky KPI strip (CPU / RAM / Disk / Mongo / Uptime)
 *  • Tabs: Overview · Processes · Database · App · Raw (super_admin)
 *  • Auto-refresh every 5s while the page is visible
 *  • 24h trend chart pulls from /history (60s samples)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Legend, BarChart, Bar,
} from 'recharts';
import {
  Cpu, MemoryStick, HardDrive, Database, Activity, Clock, RefreshCw,
  Server, Network, Layers, FileText, AlertTriangle, ChevronRight,
  Box,
} from 'lucide-react';
import { useAuth } from '../../App';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BASE_URL });
api.interceptors.request.use((c) => {
  const t =
    localStorage.getItem('su_token') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

// ─── formatting helpers ─────────────────────────────────────────────────────
const fmtBytes = (n) => {
  if (n == null) return '—';
  const k = 1024;
  if (n < k) return `${n} B`;
  if (n < k * k) return `${(n / k).toFixed(1)} KB`;
  if (n < k * k * k) return `${(n / k / k).toFixed(1)} MB`;
  return `${(n / k / k / k).toFixed(2)} GB`;
};

const fmtUptime = (secs) => {
  if (secs == null) return '—';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const fmtNumber = (n) => (n == null ? '—' : n.toLocaleString('en-IN'));

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
};

// ─── small UI atoms ─────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, percent, color = 'gold', testId }) {
  const palette = {
    gold: { ring: 'border-[#D4A843]/40', icon: 'text-[#D4A843]', bg: 'bg-[#1a1814]' },
    emerald: { ring: 'border-emerald-500/30', icon: 'text-emerald-400', bg: 'bg-emerald-500/5' },
    amber: { ring: 'border-amber-500/30', icon: 'text-amber-400', bg: 'bg-amber-500/5' },
    red: { ring: 'border-red-500/30', icon: 'text-red-400', bg: 'bg-red-500/5' },
  }[color] || { ring: 'border-[#22221F]', icon: 'text-white/60', bg: 'bg-[#1a1814]' };

  return (
    <Card data-testid={testId} className={`p-5 border ${palette.ring} bg-[#101013]`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#9A9A92]">{label}</span>
        <span className={`w-8 h-8 rounded-full flex items-center justify-center border ${palette.ring} ${palette.bg}`}>
          <Icon size={14} className={palette.icon} />
        </span>
      </div>
      <div className="text-2xl font-semibold tabular-nums text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-[#7A7A72]">{sub}</div>}
      {percent != null && (
        <div className="mt-3">
          <Progress
            value={percent}
            className="h-1.5 bg-[#1A1A1A] [&>div]:bg-[#D4A843]"
          />
        </div>
      )}
    </Card>
  );
}

function SectionCard({ title, icon: Icon, children, action, testId }) {
  return (
    <Card data-testid={testId} className="bg-[#101013] border-[#22221F] overflow-hidden">
      <div className="px-5 py-4 border-b border-[#22221F] flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white tracking-wide uppercase flex items-center gap-2">
          {Icon && <Icon size={14} className="text-[#D4A843]" />}
          {title}
        </h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

// ─── PAGE ───────────────────────────────────────────────────────────────────
export default function ServerUsagePage() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role === 'super_admin');

  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [rawMongo, setRawMongo] = useState(null);
  const [logService, setLogService] = useState('backend.err');
  const [logLines, setLogLines] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // ── Fetchers ─────────────────────────────────────────────────────────────
  const fetchLive = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/server/stats');
      setStats(r.data);
    } catch (err) {
      if (loading) toast.error(err?.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const fetchHistory = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/server/history', { params: { hours: 24 } });
      setHistory(r.data.samples || []);
    } catch (err) {
      // history is non-critical; ignore
    }
  }, []);

  const fetchRawMongo = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/server/raw/mongo');
      setRawMongo(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load mongo serverStatus');
    }
  }, []);

  const fetchLogs = useCallback(async (svc = logService) => {
    setLogLoading(true);
    try {
      const r = await api.get('/api/admin/server/raw/logs', {
        params: { service: svc, lines: 300 },
      });
      setLogLines(r.data.lines || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load log');
    } finally {
      setLogLoading(false);
    }
  }, [logService]);

  useEffect(() => {
    fetchLive();
    fetchHistory();
  }, [fetchLive, fetchHistory]);

  // 5s auto-refresh while visible
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) {
        fetchLive();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const t = setInterval(() => {
      if (!document.hidden) fetchLive();
    }, 5000);
    // history less frequent (every 60s while visible)
    const h = setInterval(() => {
      if (!document.hidden) fetchHistory();
    }, 60_000);
    return () => {
      clearInterval(t);
      clearInterval(h);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchLive, fetchHistory]);

  // ── derived KPIs ─────────────────────────────────────────────────────────
  const cpu = stats?.system?.cpu;
  const mem = stats?.system?.memory;
  const appDisk = useMemo(
    () => (stats?.disk?.mounts || []).find((m) => m.label === 'app'),
    [stats]
  );
  const mongo = stats?.mongo;

  const cpuColor = cpu && cpu.percent >= 80 ? 'red' : cpu && cpu.percent >= 60 ? 'amber' : 'emerald';
  const memColor = mem && mem.percent >= 80 ? 'red' : mem && mem.percent >= 60 ? 'amber' : 'gold';
  const diskColor = appDisk && appDisk.percent >= 80 ? 'red' : appDisk && appDisk.percent >= 60 ? 'amber' : 'gold';

  // History chart data
  const chartData = useMemo(
    () =>
      (history || []).map((row) => ({
        t: fmtTime(row.captured_at),
        cpu: row.cpu_pct,
        mem: row.mem_pct,
        disk: row.disk_app_pct,
        load: row.load_1m,
      })),
    [history]
  );

  return (
    <AdminShell title="Server Usage">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h2 className="font-['EB_Garamond'] text-2xl text-white">Server Usage</h2>
            <p className="text-sm text-[#9A9A92] mt-1 max-w-2xl">
              Live resource utilisation, database footprint and application heartbeat for the Scratch platform.
              Snapshots every 5 seconds; 24-hour history sampled every minute.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge
              variant="outline"
              className="border-emerald-500/40 text-emerald-400 bg-emerald-500/5 text-[10px] tracking-wider uppercase"
              data-testid="auto-refresh-pill"
            >
              <Activity size={10} className="mr-1 animate-pulse" />
              Live · 5s
            </Badge>
            <Button
              data-testid="refresh-server-btn"
              variant="outline"
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:text-white hover:bg-[#1A1A1A]"
              onClick={() => { fetchLive(); fetchHistory(); }}
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
        </div>

        {/* KPI strip */}
        {loading && !stats ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-32 bg-[#1A1A1A]" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiCard
              testId="kpi-cpu"
              icon={Cpu}
              label="CPU"
              value={`${cpu?.percent ?? 0}%`}
              sub={`${cpu?.cores_allocated ?? cpu?.logical_cores ?? 0} cores · load ${stats?.system?.load_avg?.['1m']?.toFixed(2) ?? '—'}`}
              percent={cpu?.percent}
              color={cpuColor}
            />
            <KpiCard
              testId="kpi-memory"
              icon={MemoryStick}
              label="Memory"
              value={`${mem?.percent ?? 0}%`}
              sub={`${fmtBytes(mem?.used_bytes)} / ${fmtBytes(mem?.total_bytes)}${mem?.is_cgroup_limited ? ' · pod limit' : ''}`}
              percent={mem?.percent}
              color={memColor}
            />
            <KpiCard
              testId="kpi-disk"
              icon={HardDrive}
              label="Disk (/app)"
              value={`${appDisk?.percent ?? 0}%`}
              sub={`${fmtBytes(appDisk?.used_bytes)} / ${fmtBytes(appDisk?.total_bytes)}`}
              percent={appDisk?.percent}
              color={diskColor}
            />
            <KpiCard
              testId="kpi-mongo"
              icon={Database}
              label="MongoDB"
              value={fmtBytes(mongo?.db?.storage_size_bytes)}
              sub={`${fmtNumber(mongo?.db?.objects)} docs · ${mongo?.db?.collections ?? 0} cols`}
              color="gold"
            />
            <KpiCard
              testId="kpi-uptime"
              icon={Clock}
              label="Container Uptime"
              value={fmtUptime(stats?.system?.uptime_seconds)}
              sub={`Mongo ${fmtUptime(mongo?.uptime_seconds)}`}
              color="gold"
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#101013] border border-[#22221F] p-1 h-auto flex-wrap">
            {[
              ['overview', 'Overview', Activity],
              ['processes', 'Processes', Layers],
              ['database', 'Database', Database],
              ['app', 'Application', Box],
              ...(isSuperAdmin ? [['raw', 'Raw Debug', AlertTriangle]] : []),
            ].map(([id, label, Icon]) => (
              <TabsTrigger
                key={id}
                value={id}
                data-testid={`server-tab-${id}`}
                className="data-[state=active]:bg-[#1a1814] data-[state=active]:text-[#D4A843] data-[state=active]:shadow-none text-[#B9B9B9] hover:text-white px-4 py-2 text-xs uppercase tracking-wider"
              >
                <Icon size={12} className="mr-1.5" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ── OVERVIEW ─────────────────────────────────────────────────── */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* 24h trend chart */}
            <SectionCard title="24-Hour Trend" icon={Activity} testId="server-history-chart">
              {chartData.length < 2 ? (
                <div className="py-12 text-center text-sm text-[#7A7A72]" data-testid="history-empty-state">
                  <Activity size={20} className="mx-auto mb-2 text-[#D4A843]/60" />
                  Collecting data… the chart will populate once a few samples have accumulated (60s cadence).
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
                      <CartesianGrid stroke="#22221F" strokeDasharray="3 3" />
                      <XAxis dataKey="t" stroke="#7A7A72" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#7A7A72" tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                      <Tooltip
                        contentStyle={{ background: '#0A0A0A', border: '1px solid #22221F', color: '#F2F2F2' }}
                        labelStyle={{ color: '#D4A843' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11, color: '#9A9A92' }} />
                      <Line type="monotone" dataKey="cpu" name="CPU %" stroke="#D4A843" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="mem" name="Memory %" stroke="#88B388" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="disk" name="Disk %" stroke="#7494C3" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </SectionCard>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Per-core CPU */}
              <SectionCard title="Per-Core CPU" icon={Cpu} testId="cpu-per-core">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(cpu?.per_core || []).map((p, idx) => (
                    <div key={idx} className="bg-[#0A0A0A] border border-[#22221F] rounded-lg p-3">
                      <div className="text-[10px] uppercase tracking-wider text-[#7A7A72]">Core {idx}</div>
                      <div className="text-lg font-semibold text-white tabular-nums mt-0.5">{p?.toFixed?.(1) ?? p}%</div>
                      <Progress value={p} className="h-1 mt-1.5 bg-[#1A1A1A] [&>div]:bg-[#D4A843]" />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-[#7A7A72] mt-3">
                  Pod is limited to {cpu?.cores_allocated ?? '—'} cores out of {cpu?.logical_cores ?? '—'} host cores via cgroup.
                </p>
              </SectionCard>

              {/* Disks */}
              <SectionCard title="Disk Mounts" icon={HardDrive} testId="disk-mounts">
                <ul className="space-y-3">
                  {(stats?.disk?.mounts || []).map((m) => (
                    <li key={m.path} className="space-y-1.5" data-testid={`disk-${m.label}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-white font-medium">{m.label}</span>
                        <span className="text-[#9A9A92] tabular-nums">
                          {fmtBytes(m.used_bytes)} / {fmtBytes(m.total_bytes)}
                          <span className={`ml-2 ${m.percent >= 80 ? 'text-red-400' : m.percent >= 60 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {m.percent}%
                          </span>
                        </span>
                      </div>
                      <Progress value={m.percent} className="h-1 bg-[#1A1A1A] [&>div]:bg-[#D4A843]" />
                      <p className="text-[10px] text-[#7A7A72] font-mono">{m.path}</p>
                    </li>
                  ))}
                </ul>
                {stats?.disk?.io_since_boot && (
                  <div className="mt-4 pt-3 border-t border-[#22221F] text-[11px] text-[#9A9A92] flex justify-between">
                    <span>Read since boot: <strong className="text-white">{fmtBytes(stats.disk.io_since_boot.read_bytes)}</strong></span>
                    <span>Write since boot: <strong className="text-white">{fmtBytes(stats.disk.io_since_boot.write_bytes)}</strong></span>
                  </div>
                )}
              </SectionCard>
            </div>

            {/* Network */}
            <SectionCard title="Network I/O (since pod start)" icon={Network} testId="network-io">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#7A7A72]">Bytes Sent</div>
                  <div className="text-lg font-semibold text-white">{fmtBytes(stats?.network?.bytes_sent)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#7A7A72]">Bytes Received</div>
                  <div className="text-lg font-semibold text-white">{fmtBytes(stats?.network?.bytes_recv)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#7A7A72]">Packets Sent</div>
                  <div className="text-lg font-semibold text-white tabular-nums">{fmtNumber(stats?.network?.packets_sent)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#7A7A72]">Packets Received</div>
                  <div className="text-lg font-semibold text-white tabular-nums">{fmtNumber(stats?.network?.packets_recv)}</div>
                </div>
              </div>
            </SectionCard>
          </TabsContent>

          {/* ── PROCESSES ────────────────────────────────────────────────── */}
          <TabsContent value="processes" className="space-y-4 mt-4">
            <SectionCard title="Top Processes by Memory" icon={Layers} testId="process-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[#7A7A72] border-b border-[#22221F]">
                      <th className="px-3 py-2 font-medium">PID</th>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">User</th>
                      <th className="px-3 py-2 font-medium text-right">RSS</th>
                      <th className="px-3 py-2 font-medium text-right">VMS</th>
                      <th className="px-3 py-2 font-medium text-right">CPU %</th>
                      <th className="px-3 py-2 font-medium">Cmd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.processes || []).map((p) => (
                      <tr key={p.pid} className="border-b border-[#1A1A1A] hover:bg-[#15151A]" data-testid={`process-row-${p.pid}`}>
                        <td className="px-3 py-2 font-mono text-[#B9B9B9]">{p.pid}</td>
                        <td className="px-3 py-2 text-white">{p.name}</td>
                        <td className="px-3 py-2 text-[#9A9A92]">{p.user}</td>
                        <td className="px-3 py-2 text-right text-white tabular-nums">{fmtBytes(p.rss_bytes)}</td>
                        <td className="px-3 py-2 text-right text-[#9A9A92] tabular-nums">{fmtBytes(p.vms_bytes)}</td>
                        <td className="px-3 py-2 text-right text-white tabular-nums">{p.cpu_percent?.toFixed(1)}</td>
                        <td className="px-3 py-2 text-[11px] text-[#7A7A72] font-mono truncate max-w-xs" title={p.cmdline}>
                          {p.cmdline}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-[#7A7A72] mt-3">
                Filtered to platform-relevant processes (uvicorn / python / node / yarn / mongod / supervisord / nginx).
              </p>
            </SectionCard>
          </TabsContent>

          {/* ── DATABASE ────────────────────────────────────────────────── */}
          <TabsContent value="database" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <KpiCard
                testId="db-data-size"
                icon={Database}
                label="Data Size"
                value={fmtBytes(mongo?.db?.data_size_bytes)}
                sub={`Avg ${fmtBytes(mongo?.db?.avg_obj_size_bytes)}/doc`}
              />
              <KpiCard
                testId="db-storage-size"
                icon={HardDrive}
                label="Storage on Disk"
                value={fmtBytes(mongo?.db?.storage_size_bytes)}
                sub={`Indexes ${fmtBytes(mongo?.db?.index_size_bytes)}`}
              />
              <KpiCard
                testId="db-connections"
                icon={Network}
                label="Connections"
                value={fmtNumber(mongo?.connections?.current)}
                sub={`${fmtNumber(mongo?.connections?.available)} available · ${fmtNumber(mongo?.connections?.totalCreated)} created`}
              />
            </div>

            <SectionCard title="Top Collections by Storage" icon={Layers} testId="db-collections">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[#7A7A72] border-b border-[#22221F]">
                      <th className="px-3 py-2 font-medium">Collection</th>
                      <th className="px-3 py-2 font-medium text-right">Docs</th>
                      <th className="px-3 py-2 font-medium text-right">Data</th>
                      <th className="px-3 py-2 font-medium text-right">Storage</th>
                      <th className="px-3 py-2 font-medium text-right">Indexes</th>
                      <th className="px-3 py-2 font-medium text-right">Index Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(mongo?.collections || []).map((c) => (
                      <tr key={c.name} className="border-b border-[#1A1A1A] hover:bg-[#15151A]" data-testid={`collection-row-${c.name}`}>
                        <td className="px-3 py-2 text-white font-mono text-xs">{c.name}</td>
                        <td className="px-3 py-2 text-right text-white tabular-nums">{fmtNumber(c.count)}</td>
                        <td className="px-3 py-2 text-right text-[#9A9A92] tabular-nums">{fmtBytes(c.size_bytes)}</td>
                        <td className="px-3 py-2 text-right text-white tabular-nums">{fmtBytes(c.storage_size_bytes)}</td>
                        <td className="px-3 py-2 text-right text-[#9A9A92] tabular-nums">{c.nindexes}</td>
                        <td className="px-3 py-2 text-right text-[#9A9A92] tabular-nums">{fmtBytes(c.total_index_size_bytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Operation Counters" icon={Activity} testId="db-opcounters">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(mongo?.opcounters || {}).map(([k, v]) => (
                  <div key={k} className="bg-[#0A0A0A] border border-[#22221F] rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#7A7A72]">{k}</div>
                    <div className="text-lg font-semibold text-white tabular-nums mt-0.5">{fmtNumber(v)}</div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#7A7A72] mt-3">
                Cumulative since MongoDB process started ({fmtUptime(mongo?.uptime_seconds)} ago, v{mongo?.mongo_version || '—'}).
              </p>
            </SectionCard>
          </TabsContent>

          {/* ── APPLICATION ─────────────────────────────────────────────── */}
          <TabsContent value="app" className="space-y-4 mt-4">
            <SectionCard title="Business Collections" icon={Box} testId="app-collection-counts">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(stats?.app?.counts || {}).map(([k, v]) => (
                  <div key={k} className="bg-[#0A0A0A] border border-[#22221F] rounded-lg p-3" data-testid={`count-${k}`}>
                    <div className="text-[10px] uppercase tracking-wider text-[#7A7A72] truncate">{k.replace(/_/g, ' ')}</div>
                    <div className="text-2xl font-semibold text-white tabular-nums mt-0.5">{fmtNumber(v)}</div>
                  </div>
                ))}
              </div>
            </SectionCard>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Last 24 Hours" icon={Clock} testId="app-24h">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#9A9A92]">Confirmed bookings</span>
                    <span className="text-white font-semibold tabular-nums">{fmtNumber(stats?.app?.bookings_24h)}</span>
                  </div>
                  <div className="pt-3 border-t border-[#22221F]">
                    <div className="text-[10px] uppercase tracking-wider text-[#7A7A72] mb-2">WhatsApp delivery (24h)</div>
                    {Object.entries(stats?.app?.whatsapp_24h || {}).length === 0 ? (
                      <p className="text-xs text-[#7A7A72]">No WhatsApp activity in the last 24 hours.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(stats?.app?.whatsapp_24h || {}).map(([status, count]) => {
                          const color = {
                            sent: 'border-[#D4A843]/40 text-[#F1E2B8]',
                            delivered: 'border-emerald-500/40 text-emerald-400',
                            read: 'border-emerald-500/40 text-emerald-400',
                            failed: 'border-red-500/40 text-red-400',
                          }[status] || 'border-white/15 text-white/60';
                          return (
                            <Badge key={status} variant="outline" className={`${color} text-xs tracking-wider uppercase`}>
                              {status}: {count}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Process Heartbeats" icon={Server} testId="app-services">
                <ul className="space-y-2 text-sm">
                  {[
                    ['Backend (uvicorn)', stats?.processes?.find((p) => p.name?.toLowerCase().includes('uvicorn') || p.cmdline?.includes('uvicorn'))],
                    ['Frontend (node/yarn)', stats?.processes?.find((p) => p.name === 'node' || p.cmdline?.includes('yarn'))],
                    ['MongoDB', stats?.processes?.find((p) => p.name?.toLowerCase().includes('mongod'))],
                  ].map(([label, proc]) => (
                    <li key={label} className="flex items-center justify-between py-2 border-b border-[#1A1A1A] last:border-0">
                      <span className="flex items-center gap-2 text-[#B9B9B9]">
                        <span className={`w-2 h-2 rounded-full ${proc ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        {label}
                      </span>
                      <span className="text-xs text-[#7A7A72] tabular-nums">
                        {proc ? `pid ${proc.pid} · ${fmtBytes(proc.rss_bytes)}` : 'not running'}
                      </span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            </div>
          </TabsContent>

          {/* ── RAW DEBUG (super_admin) ─────────────────────────────────── */}
          {isSuperAdmin && (
            <TabsContent value="raw" className="space-y-4 mt-4">
              <SectionCard
                title="Mongo serverStatus"
                icon={Database}
                testId="raw-mongo-card"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchRawMongo}
                    className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:text-white hover:bg-[#1A1A1A]"
                    data-testid="load-raw-mongo-btn"
                  >
                    <RefreshCw size={12} className="mr-1.5" />
                    {rawMongo ? 'Reload' : 'Load'}
                  </Button>
                }
              >
                {!rawMongo ? (
                  <p className="text-sm text-[#7A7A72]">
                    Click <em>Load</em> to fetch the full `db.command(&quot;serverStatus&quot;)` payload (minus
                    `metrics`, `wiredTiger`, `tcmalloc` for size).
                  </p>
                ) : !rawMongo.ok ? (
                  <p className="text-sm text-red-400">{rawMongo.error || 'failed to load'}</p>
                ) : (
                  <ScrollArea className="h-[500px] rounded border border-[#22221F] bg-[#0A0A0A]">
                    <pre className="text-[11px] text-[#B9B9B9] font-mono p-4 leading-relaxed whitespace-pre-wrap break-words">
                      {JSON.stringify(rawMongo.data, null, 2)}
                    </pre>
                  </ScrollArea>
                )}
              </SectionCard>

              <SectionCard
                title="Supervisor Logs"
                icon={FileText}
                testId="raw-logs-card"
                action={
                  <div className="flex items-center gap-2">
                    <select
                      data-testid="log-service-select"
                      value={logService}
                      onChange={(e) => { setLogService(e.target.value); fetchLogs(e.target.value); }}
                      className="bg-[#0A0A0A] border border-[#22221F] text-white text-xs rounded px-2 py-1.5"
                    >
                      {['backend.out','backend.err','frontend.out','frontend.err','mongodb.out','mongodb.err'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchLogs()}
                      className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:text-white hover:bg-[#1A1A1A]"
                      data-testid="load-logs-btn"
                    >
                      <RefreshCw size={12} className={`mr-1.5 ${logLoading ? 'animate-spin' : ''}`} />
                      Tail 300
                    </Button>
                  </div>
                }
              >
                {logLines.length === 0 ? (
                  <p className="text-sm text-[#7A7A72]">
                    {logLoading ? 'Loading…' : 'Pick a service and click Tail 300 to view the most recent log lines.'}
                  </p>
                ) : (
                  <ScrollArea className="h-[500px] rounded border border-[#22221F] bg-[#0A0A0A]">
                    <pre className="text-[11px] text-[#B9B9B9] font-mono p-4 leading-relaxed">
                      {logLines.join('\n')}
                    </pre>
                  </ScrollArea>
                )}
              </SectionCard>
            </TabsContent>
          )}
        </Tabs>

        {stats?.captured_at && (
          <p className="text-[11px] text-[#7A7A72] text-right">
            Last sampled <strong className="text-[#B9B9B9]">{fmtTime(stats.captured_at)}</strong> IST · history retained 24h
          </p>
        )}
      </div>
    </AdminShell>
  );
}
